import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { getMainScrollContainer, scrollMainToTop } from '@/shared/lib/scrollContainer'

const SCROLL_STORAGE_KEY = 'synap-scroll-positions'
const SCROLL_SAVE_THROTTLE_MS = 120

type ScrollPositionMap = Record<string, number>

const readStoredPositions = (): ScrollPositionMap => {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as ScrollPositionMap
  } catch {
    return {}
  }
}

const writeStoredPositions = (positions: ScrollPositionMap): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions))
  } catch {
    /* quota ou modo privado — ignorar */
  }
}

const getSavedScrollTop = (locationKey: string, memory: Map<string, number>): number | null => {
  if (memory.has(locationKey)) return memory.get(locationKey) ?? null
  const stored = readStoredPositions()[locationKey]
  return typeof stored === 'number' && Number.isFinite(stored) ? stored : null
}

const persistScrollTop = (locationKey: string, scrollTop: number, memory: Map<string, number>): void => {
  memory.set(locationKey, scrollTop)
  const next = { ...readStoredPositions(), [locationKey]: scrollTop }
  writeStoredPositions(next)
}

const restoreScrollTop = (container: HTMLElement, scrollTop: number): void => {
  const apply = () => {
    container.scrollTop = scrollTop
  }

  apply()
  requestAnimationFrame(() => {
    apply()
    requestAnimationFrame(apply)
  })
}

/** Restaura a posição de scroll do container principal em navegação POP (R15). */
export const useScrollRestoration = (): void => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const memoryRef = useRef<Map<string, number>>(new Map())
  const previousLocationKeyRef = useRef<string | null>(null)
  const throttleTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const container = getMainScrollContainer()
    if (!container) return

    const saveForKey = (locationKey: string) => {
      persistScrollTop(locationKey, container.scrollTop, memoryRef.current)
    }

    const handleScroll = () => {
      if (throttleTimerRef.current !== null) return
      throttleTimerRef.current = window.setTimeout(() => {
        throttleTimerRef.current = null
        saveForKey(location.key)
      }, SCROLL_SAVE_THROTTLE_MS)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (throttleTimerRef.current !== null) {
        window.clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }
      saveForKey(location.key)
    }
  }, [location.key])

  useLayoutEffect(() => {
    const container = getMainScrollContainer()
    if (!container) return

    const previousKey = previousLocationKeyRef.current
    if (previousKey && previousKey !== location.key) {
      persistScrollTop(previousKey, container.scrollTop, memoryRef.current)
    }
    previousLocationKeyRef.current = location.key

    if (navigationType === 'POP') {
      const saved = getSavedScrollTop(location.key, memoryRef.current)
      if (saved != null) {
        restoreScrollTop(container, saved)
        return
      }
    }

    scrollMainToTop({ behavior: 'auto' })
  }, [location.key, navigationType])
}
